import { useCallback, useEffect, useRef, useState } from 'react';
import type { Camera, Regiao } from '@core/tipos';
import {
  CAMERA_INICIAL,
  ZOOM_MAX,
  ZOOM_MIN,
  aplicarZoom,
  centralizarEm,
  mover,
  precisaRecentralizar,
  regiaoVisivel,
} from '@core/tabuleiro/camera';

/**
 * Câmera do tabuleiro: arrastar, zoom e recentralizar (§7.2, T07).
 *
 * Arrastar altera só a câmera — coordenadas de palavras nunca mudam (T07-01).
 *
 * O gesto começa em qualquer lugar do tabuleiro, inclusive sobre uma letra ou
 * uma sugestão (§14): no celular o tabuleiro fica coberto de células, e exigir
 * área vazia deixaria o dedo sem lugar para arrastar. O que separa um toque de
 * um arrasto é a distância percorrida — abaixo de `LIMIAR_ARRASTO` o gesto
 * ainda é um clique, acima dele a câmera assume e o clique é descartado.
 */

/** Distância, em pixels, que transforma um toque em arrasto. */
const LIMIAR_ARRASTO = 8;

export interface UsoCamera {
  readonly camera: Camera;
  readonly animando: boolean;
  readonly arrastando: boolean;
  readonly viewport: { largura: number; altura: number };
  readonly precisaVoltar: (areaAtiva: Regiao) => boolean;
  readonly recentralizar: (areaAtiva: Regiao) => void;
  readonly aoZoom: (fator: number, x: number, y: number) => void;
  readonly ajustarZoom: (delta: number) => void;
  readonly manipuladores: {
    /* Captura: o tabuleiro precisa ver o toque antes dos botões que contém,
       senão arrastar a partir de uma sugestão seria impossível. */
    onPointerDownCapture: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: (e: React.PointerEvent) => void;
    onPointerCancel: (e: React.PointerEvent) => void;
    onClickCapture: (e: React.MouseEvent) => void;
    onWheel: (e: React.WheelEvent) => void;
  };
}

interface Ponteiro {
  readonly x: number;
  readonly y: number;
}

export function useCamera(elemento: React.RefObject<HTMLElement | null>): UsoCamera {
  const [camera, setCamera] = useState<Camera>(CAMERA_INICIAL);
  const [animando, setAnimando] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const [viewport, setViewport] = useState({ largura: 1024, altura: 768 });

  const ponteiros = useRef(new Map<number, Ponteiro>());
  const origens = useRef(new Map<number, Ponteiro>());
  const distanciaPinca = useRef<number | null>(null);
  /* O `click` sintetizado no fim do gesto chega depois do `pointerup` e
     precisa encontrar esta marca; ela cai logo em seguida. */
  const arrastou = useRef(false);

  /* Mede a viewport e acompanha redimensionamento (§14). */
  useEffect(() => {
    const alvo = elemento.current;
    if (alvo === null) return;

    const medir = () => {
      const caixa = alvo.getBoundingClientRect();
      setViewport({ largura: caixa.width, altura: caixa.height });
    };
    medir();

    const observador = new ResizeObserver(medir);
    observador.observe(alvo);
    return () => observador.disconnect();
  }, [elemento]);

  const recentralizar = useCallback(
    (areaAtiva: Regiao) => {
      setAnimando(true);
      setCamera((atual) => centralizarEm(atual, areaAtiva, viewport.largura, viewport.altura));
      // A transição da câmera dura 240–360 ms (§15); desligar depois evita
      // que um arrasto imediato herde a animação.
      window.setTimeout(() => setAnimando(false), 360);
    },
    [viewport.largura, viewport.altura],
  );

  const precisaVoltar = useCallback(
    (areaAtiva: Regiao) =>
      precisaRecentralizar(areaAtiva, regiaoVisivel(camera, viewport.largura, viewport.altura)),
    [camera, viewport.largura, viewport.altura],
  );

  const aoZoom = useCallback((fator: number, x: number, y: number) => {
    setAnimando(false);
    setCamera((atual) => aplicarZoom(atual, fator, x, y));
  }, []);

  const ajustarZoom = useCallback(
    (delta: number) => {
      setAnimando(true);
      setCamera((atual) =>
        aplicarZoom(
          atual,
          (atual.zoom + delta) / atual.zoom,
          viewport.largura / 2,
          viewport.altura / 2,
        ),
      );
      window.setTimeout(() => setAnimando(false), 360);
    },
    [viewport.largura, viewport.altura],
  );

  const posicaoLocal = useCallback(
    (e: React.PointerEvent | React.WheelEvent) => {
      const alvo = elemento.current;
      if (alvo === null) return { x: e.clientX, y: e.clientY };
      const caixa = alvo.getBoundingClientRect();
      return { x: e.clientX - caixa.left, y: e.clientY - caixa.top };
    },
    [elemento],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      // T07-06: o tabuleiro é superfície de navegação, não destino de foco.
      // Sem isto, o mousedown de compatibilidade tira o foco do campo e as
      // teclas seguintes se perdem. Vale também sobre os botões do tabuleiro:
      // impedir o `mousedown` não impede o `click` deles.
      e.preventDefault();

      const posicao = posicaoLocal(e);
      ponteiros.current.set(e.pointerId, posicao);
      origens.current.set(e.pointerId, posicao);
      arrastou.current = false;
      setAnimando(false);
    },
    [posicaoLocal],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const anterior = ponteiros.current.get(e.pointerId);
      if (anterior === undefined) return;

      const atual = posicaoLocal(e);
      ponteiros.current.set(e.pointerId, atual);

      if (ponteiros.current.size >= 2) {
        // Pinça: zoom em torno do ponto médio dos dois dedos (T07-02). Dois
        // dedos na tela nunca são um clique, então não passam pelo limiar.
        const [a, b] = [...ponteiros.current.values()];
        if (a === undefined || b === undefined) return;
        arrastou.current = true;
        const distancia = Math.hypot(a.x - b.x, a.y - b.y);
        const anteriorDistancia = distanciaPinca.current;
        distanciaPinca.current = distancia;
        if (anteriorDistancia !== null && anteriorDistancia > 0) {
          aoZoom(distancia / anteriorDistancia, (a.x + b.x) / 2, (a.y + b.y) / 2);
        }
        return;
      }

      if (!arrastou.current) {
        const origem = origens.current.get(e.pointerId);
        if (origem === undefined) return;
        if (Math.hypot(atual.x - origem.x, atual.y - origem.y) < LIMIAR_ARRASTO) return;

        // A partir daqui o gesto é da câmera: a captura mantém os eventos no
        // tabuleiro mesmo quando o dedo sai dele.
        arrastou.current = true;
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        setArrastando(true);
      }

      setCamera((c) => mover(c, atual.x - anterior.x, atual.y - anterior.y));
    },
    [aoZoom, posicaoLocal],
  );

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    ponteiros.current.delete(e.pointerId);
    origens.current.delete(e.pointerId);
    if (ponteiros.current.size < 2) distanciaPinca.current = null;
    if (ponteiros.current.size !== 0) return;

    setArrastando(false);
    // O `click` do fim do gesto chega logo depois deste `pointerup`, na mesma
    // volta do laço de eventos; a marca precisa sobreviver a ele e morrer em
    // seguida. Deixá-la viva até o toque seguinte engoliria o `Enter` de quem
    // chegasse à sugestão pelo teclado.
    window.setTimeout(() => {
      arrastou.current = false;
    }, 0);
  }, []);

  /* Arrastar por cima de uma sugestão não pode escolhê-la ao soltar. */
  const onClickCapture = useCallback((e: React.MouseEvent) => {
    if (!arrastou.current) return;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onWheel = useCallback(
    (e: React.WheelEvent) => {
      const { x, y } = posicaoLocal(e);
      aoZoom(Math.exp(-e.deltaY * 0.0015), x, y);
    },
    [aoZoom, posicaoLocal],
  );

  return {
    camera,
    animando,
    arrastando,
    viewport,
    precisaVoltar,
    recentralizar,
    aoZoom,
    ajustarZoom,
    manipuladores: {
      onPointerDownCapture: onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onClickCapture,
      onWheel,
    },
  };
}

export { ZOOM_MAX, ZOOM_MIN };
